import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { Minus, Plus } from 'lucide-react';
import { LOGO_MIN_PX } from '@ready/shared';
import { Button } from './ui';
import { Modal } from './modal';

/** What the server gets: a square PNG this many pixels on a side, or smaller when the crop is. */
const OUTPUT_PX = 512;
/** The on-screen frame; the canvas is drawn at device resolution. */
const VIEW_PX = 288;

interface View {
  /** 1 = the image's short side fills the square. */
  zoom: number;
  /** Center of the crop, in image pixels. */
  cx: number;
  cy: number;
}

/**
 * Crops a picked image to a square: drag to move, zoom with the slider or
 * buttons. Drawn on a canvas (the page's CSP allows no inline styles), and
 * the result is re-encoded as PNG, which also drops any photo metadata.
 */
export function LogoCropper({
  image,
  onCancel,
  onDone,
  busy,
}: {
  image: HTMLImageElement;
  onCancel: () => void;
  onDone: (png: Blob) => void;
  busy?: boolean;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const short = Math.min(image.naturalWidth, image.naturalHeight);
  // The crop never gets smaller than the minimum logo size.
  const maxZoom = Math.max(1, Math.min(4, short / LOGO_MIN_PX));
  const [view, setView] = useState<View>({
    zoom: 1,
    cx: image.naturalWidth / 2,
    cy: image.naturalHeight / 2,
  });
  const drag = useRef<{ x: number; y: number; cx: number; cy: number } | null>(null);

  /** Side of the crop in image pixels, and the center kept inside the image. */
  const crop = (v: View) => {
    const side = short / v.zoom;
    const half = side / 2;
    const cx = Math.min(Math.max(v.cx, half), image.naturalWidth - half);
    const cy = Math.min(Math.max(v.cy, half), image.naturalHeight - half);
    return { side, sx: cx - half, sy: cy - half, cx, cy };
  };

  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    const dpr = window.devicePixelRatio || 1;
    el.width = Math.round(VIEW_PX * dpr);
    el.height = Math.round(VIEW_PX * dpr);
    const ctx = el.getContext('2d')!;
    const { side, sx, sy } = crop(view);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, el.width, el.height);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, sx, sy, side, side, 0, 0, el.width, el.height);
  });

  const setZoom = (zoom: number) =>
    setView((v) => ({ ...v, zoom: Math.min(maxZoom, Math.max(1, zoom)) }));

  const onPointerDown = (e: PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    const { cx, cy } = crop(view);
    drag.current = { x: e.clientX, y: e.clientY, cx, cy };
  };
  const onPointerMove = (e: PointerEvent<HTMLCanvasElement>) => {
    const d = drag.current;
    if (!d) return;
    // Screen pixels to image pixels: the frame shows `side` image pixels.
    const k = crop(view).side / e.currentTarget.getBoundingClientRect().width;
    setView((v) => ({ ...v, cx: d.cx - (e.clientX - d.x) * k, cy: d.cy - (e.clientY - d.y) * k }));
  };
  const onPointerUp = () => {
    drag.current = null;
    setView((v) => ({ ...v, ...crop(v) }));
  };

  const save = () => {
    const { side, sx, sy } = crop(view);
    const px = Math.round(Math.min(OUTPUT_PX, side));
    const out = document.createElement('canvas');
    out.width = px;
    out.height = px;
    const ctx = out.getContext('2d')!;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, sx, sy, side, side, 0, 0, px, px);
    out.toBlob((blob) => blob && onDone(blob), 'image/png');
  };

  return (
    <Modal title="Crop your logo" onClose={onCancel}>
      <p className="text-sm text-muted-foreground">
        Logos are square, like on LinkedIn. Drag to position it and zoom to fit.
      </p>
      <div className="mt-4 flex justify-center">
        <canvas
          ref={canvas}
          aria-label="Logo crop preview"
          className="size-72 max-w-full cursor-grab touch-none rounded-2xl border border-border active:cursor-grabbing"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          aria-label="Zoom out"
          onClick={() => setZoom(view.zoom - 0.25)}
          disabled={view.zoom <= 1}
        >
          <Minus className="size-4" aria-hidden />
        </Button>
        <input
          type="range"
          aria-label="Zoom"
          min={1}
          max={maxZoom}
          step={0.01}
          value={view.zoom}
          disabled={maxZoom <= 1}
          onChange={(e) => setZoom(Number(e.target.value))}
          className="h-10 w-full accent-primary"
        />
        <Button
          type="button"
          variant="ghost"
          aria-label="Zoom in"
          onClick={() => setZoom(view.zoom + 0.25)}
          disabled={view.zoom >= maxZoom}
        >
          <Plus className="size-4" aria-hidden />
        </Button>
      </div>
      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" onClick={save} disabled={busy}>
          {busy ? 'Uploading…' : 'Use this logo'}
        </Button>
      </div>
    </Modal>
  );
}

/** Loads a picked file as an image, or explains why it can't be a logo. */
export function loadLogoImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    if (!/^image\/(png|jpeg|webp)$/.test(file.type))
      return reject(new Error('Pick a PNG, JPEG or WebP image.'));
    if (file.size > 2 * 1024 * 1024) return reject(new Error('Pick an image of 2 MB or less.'));
    // A data: URL, not a blob: one: the site's CSP doesn't allow blob: images.
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("That image couldn't be opened."));
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        if (Math.min(img.naturalWidth, img.naturalHeight) < LOGO_MIN_PX)
          reject(
            new Error(
              `That image is too small. Logos need at least ${LOGO_MIN_PX} × ${LOGO_MIN_PX} pixels.`,
            ),
          );
        else resolve(img);
      };
      img.onerror = () => reject(new Error("That image couldn't be opened."));
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}
